/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { jsx } from '@compiled/react';
import { useSetAtom } from 'jotai';
import { useRouter, useRouterActions } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { releaseBenchmarkRunAtom } from '@atlassian/twg-benchmarking-experience/state/run';
import {
	SiteSwitcher,
	type SiteSelectedAttributes,
} from '@atlassian/twg-microsite-common/site-switcher';
import type { SiteSwitcherCloseReason } from '@atlassian/twg-microsite-common/site-types';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { BENCHMARK_ROUTE_PATH, isBenchmarkRunRouteName } from '../../../../routes';

/**
 * The shared site switcher, wired to the microsite's analytics events and to the benchmark,
 * whose run-scoped routes cannot survive a tenant change.
 */
export const NavigationSiteSwitcher = (): JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const releaseBenchmarkRun = useSetAtom(releaseBenchmarkRunAtom);

	const onOpened = useCallback((): void => {
		fireEvent('ui.dropdown.opened.twgMicrositeSiteSwitcher', {});
	}, [fireEvent]);

	const onClosed = useCallback(
		(reason: SiteSwitcherCloseReason): void => {
			fireEvent('ui.dropdown.closed.twgMicrositeSiteSwitcher', { closeReason: reason });
		},
		[fireEvent],
	);

	const onSiteSelected = useCallback(
		(attributes: SiteSelectedAttributes): void => {
			fireEvent('ui.dropdownItem.clicked.twgMicrositeSiteSwitcher', attributes);
		},
		[fireEvent],
	);

	/*
	 * A benchmark run belongs to the tenant it ran against, and the service answers `404` for its
	 * id read against any other, so a run-scoped URL is meaningless once the site changes. Every
	 * other route is tenant-agnostic and stays put.
	 *
	 * Let go of the run before the push, not merely navigate away from it: the flow sends any
	 * arrival at `/benchmark` back to the run it holds, which would land the visitor on the tenant
	 * they just left. A live run carries on and stays reachable at its own address and from the
	 * past-runs list.
	 */
	const onSiteSwitched = useCallback((): void => {
		if (fg('graphyte-twg-cloud-benchmark') && isBenchmarkRunRouteName(route?.name)) {
			releaseBenchmarkRun();
			push(BENCHMARK_ROUTE_PATH);
		}
	}, [push, releaseBenchmarkRun, route?.name]);

	return (
		<SiteSwitcher
			onOpened={onOpened}
			onClosed={onClosed}
			onSiteSelected={onSiteSelected}
			onSiteSwitched={onSiteSwitched}
		/>
	);
};
